import { Injectable } from '@angular/core';
import { LayoutModalService } from '@intrensic/shared/layout';
import { IconType } from '@shared/utils/icons';
import { MenuItemsService, NavbarHeaderMenuItem, NavbarMenuItem } from '@shared/utils/menu-items';
import { Observable, Subject, combineLatest, filter, map, of, takeUntil } from 'rxjs';
import { LoggedInOfficer, OfficerDetailFacade } from '@intrensic/shared/officer-details';
import { UserRole } from '@shared/utils/base-auth';
import { AgencyDetails, AgencyDetailsFacade } from '@shared/feature/agency-details';
import { AppConfig, ProductionTypes } from '@shared/utils/app-config';
import { BaseB2cAuthService } from '@shared/feature/b2c-auth';
import { Event, RouterEvent, Router, NavigationEnd } from '@angular/router';
import { TranslationFromJsonService } from '@shared/feature/translate';
import { AppRoutes } from '@shared/utils/navigation';

@Injectable({
  providedIn: 'root',
})
export class MenuService extends MenuItemsService {
  private readonly destroying$ = new Subject<void>();

  constructor(
    private auth: BaseB2cAuthService,
    private layoutModalService: LayoutModalService,
    private officerDetailsFacade: OfficerDetailFacade,
    private agencyDetailsFacade: AgencyDetailsFacade,
    private appConfig: AppConfig,
    private router: Router,
    private translationFromJsonService: TranslationFromJsonService
  ) {
    super();
  }

  initializeMenuService(): void {
    this.router.events
      .pipe(
        filter((e: Event | RouterEvent): e is RouterEvent => e instanceof NavigationEnd),
        takeUntil(this.destroying$)
      )
      .subscribe((e: RouterEvent) => {
        const url = e.url;
        const uuidRegex = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
        const containsUid = uuidRegex.test(url);

        if (containsUid) {
          const urlWithoutUid = url.replace(uuidRegex, '');
          localStorage.setItem('activeRoute', urlWithoutUid.slice(0, -1));
        } else {
          localStorage.setItem('activeRoute', e.url);
        }
      });
  }

  getHeaderMenuItems(): Observable<NavbarHeaderMenuItem[]> {
    if (this.appConfig.productionType === ProductionTypes.isIndustrialCam) {
      return of([
        new NavbarHeaderMenuItem(this.translationFromJsonService.instant('app-tab-change-profile'), undefined, () => this.layoutModalService.openChangeProfileModal()),
        new NavbarHeaderMenuItem(this.translationFromJsonService.instant('app-tab-change-pin'), undefined, () => this.layoutModalService.openChangePinModal()),
        new NavbarHeaderMenuItem(this.translationFromJsonService.instant('app-tab-log-out'), undefined, () => this.auth.logout()),
      ]);
    }

    return of([
      new NavbarHeaderMenuItem(this.translationFromJsonService.instant('app-tab-change-profile'), undefined, () => this.layoutModalService.openChangeProfileModal()),
      new NavbarHeaderMenuItem(this.translationFromJsonService.instant('app-tab-change-password'), undefined, () => this.layoutModalService.openPassword()),
      new NavbarHeaderMenuItem(this.translationFromJsonService.instant('app-tab-change-pin'), undefined, () => this.layoutModalService.openChangePinModal()),
      new NavbarHeaderMenuItem(this.translationFromJsonService.instant('app-tab-log-out'), undefined, () => this.auth.logout()),
    ]);
  }

  getMenuItems(): Observable<NavbarMenuItem[]> {
    return combineLatest<[LoggedInOfficer, AgencyDetails]>([this.officerDetailsFacade.getLoggedInOfficer(), this.agencyDetailsFacade.getAgencyDetails()]).pipe(
      map(([officer, agencyDetails]) => {
        const array = new Array<NavbarMenuItem>();

        if (
          this.appConfig.productionType !== ProductionTypes.isIndustrialCam &&
          (officer.role === UserRole.SuperAdmin || officer.role === UserRole.Supervisor || officer.role === UserRole.Commander)
        ) {
          array.push(new NavbarMenuItem({ label: this.translationFromJsonService.instant('app-tab-dashboard'), icon: IconType.LineChart, routerLink: AppRoutes.Dashboard }));
        }

        if (
          officer.role === UserRole.User ||
          officer.role === UserRole.Supervisor ||
          officer.role === UserRole.Commander ||
          officer.role === UserRole.Prosecutor ||
          officer.role === UserRole.SuperAdmin
        ) {
          if (this.appConfig.productionType === ProductionTypes.isPolice) {
            array.push(
              new NavbarMenuItem({
                label: this.translationFromJsonService.instant('app-tab-evidence-labeling'),
                icon: IconType.Camera,
                routerLink: `/${AppRoutes.Files}/processing`,
              })
            );
          }

          if (this.appConfig.productionType === ProductionTypes.isIndustrialCam && !this.appConfig.production) {
            array.push(
              new NavbarMenuItem({
                label: this.translationFromJsonService.instant('app-tab-live'),
                icon: IconType.FilledCircle,
                iconClass: 'text-danger',
                routerLink: AppRoutes.Live,
              })
            );
          }

          array.push(
            new NavbarMenuItem({
              label: this.translationFromJsonService.instant('app-tab-title-files'),
              icon: IconType.File,
              routerLink: '',
              items: [
                new NavbarMenuItem({ label: this.translationFromJsonService.instant('app-tab-files-list'), icon: IconType.Copy, routerLink: AppRoutes.Files }),
                new NavbarMenuItem({ label: this.translationFromJsonService.instant('app-tab-video-browser'), icon: IconType.Camera, routerLink: AppRoutes.Videos }),
                new NavbarMenuItem({ label: this.translationFromJsonService.instant('app-tab-pending-removal'), icon: IconType.Clock, routerLink: `/${AppRoutes.Files}/deletion` }),
                new NavbarMenuItem({ label: this.translationFromJsonService.instant('app-tab-files-upload'), icon: IconType.Upload, routerLink: `/${AppRoutes.Files}/upload` }),
              ],
            })
          );

          array.push(new NavbarMenuItem({ label: this.translationFromJsonService.instant('app-tab-title-folders'), icon: IconType.Folder, routerLink: AppRoutes.Folders }));
        }

        if (officer.role === UserRole.SuperAdmin && this.appConfig.productionType === ProductionTypes.isIndustrialCam && !this.appConfig.production) {
          array.push(
            new NavbarMenuItem({
              label: this.translationFromJsonService.instant('app-tab-title-events'),
              icon: IconType.Bolt,
              routerLink: AppRoutes.Events,
            })
          );
        }

        if (officer.role !== UserRole.HRAdmin) {
          array.push(
            new NavbarMenuItem({
              label: this.translationFromJsonService.instant('tabs-simple-tab-shares'),
              icon: IconType.ShareNodes,
              routerLink: '',
              items: [
                new NavbarMenuItem({
                  label: this.translationFromJsonService.instant('app-tab-shared-with-me'),
                  icon: IconType.Users,
                  routerLink: AppRoutes.Files,
                  queryParams: { pageType: 1 },
                }),
                new NavbarMenuItem({ label: this.translationFromJsonService.instant('app-tab-shared-by-me'), icon: IconType.Send, routerLink: AppRoutes.Shared }),
              ],
            })
          );
        }

        if (officer.role === UserRole.SuperAdmin || officer.role === UserRole.HRAdmin) {
          array.push(
            new NavbarMenuItem({
              label: this.translationFromJsonService.instant('app-tab-organization'),
              icon: IconType.Building,
              routerLink: '',
              items: [
                new NavbarMenuItem({
                  label: this.translationFromJsonService.instant('app-tab-manage-general-settings'),
                  icon: IconType.LineChart,
                  routerLink: AppRoutes.Customers,
                }),
                new NavbarMenuItem({
                  label: this.translationFromJsonService.instant('app-tab-manage-users'),
                  icon: IconType.Users,
                  routerLink: AppRoutes.Users,
                }),
                new NavbarMenuItem({
                  label: this.translationFromJsonService.instant('app-tab-device-list'),
                  icon: IconType.Laptop,
                  routerLink: AppRoutes.Devices,
                }),
                new NavbarMenuItem({
                  label: this.translationFromJsonService.instant('app-tab-categories'),
                  icon: IconType.Tags,
                  routerLink: AppRoutes.Categories,
                }),
                new NavbarMenuItem({
                  label: this.translationFromJsonService.instant('app-title-organization-chart'),
                  icon: IconType.Sitemap,
                  routerLink: AppRoutes.OrganizationChart,
                }),
              ],
            })
          );
        }

        if (officer.role === UserRole.SuperAdmin || officer.role === UserRole.Commander) {
          array.push(
            new NavbarMenuItem({
              label: this.translationFromJsonService.instant('app-tab-report'),
              icon: IconType.Book,
              routerLink: '',
              items: [
                new NavbarMenuItem({ label: this.translationFromJsonService.instant('app-tab-daily-reports'), icon: IconType.Calendar, routerLink: `${AppRoutes.Reports}/daily` }),
                new NavbarMenuItem({
                  label: this.translationFromJsonService.instant('app-tab-summary-reports'),
                  icon: IconType.BarChart,
                  routerLink: `${AppRoutes.Reports}/summary`,
                }),
              ],
            })
          );
        }
        if (agencyDetails.canDownloadClient && (this.appConfig.productionType !== ProductionTypes.isIndustrialCam || !this.appConfig.production)) {
          array.push(new NavbarMenuItem({ label: this.translationFromJsonService.instant('app-tab-title-uploader'), icon: IconType.Download, routerLink: AppRoutes.Downloads }));
        }

        return array;
      })
    );
  }

  public onDestroy(): void {
    this.destroying$.next();
    this.destroying$.complete();
  }
}
